import React, {FC} from "react"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {
	CheckDeposit,
	CheckDepositStatus,
	CheckDepositVendors,
	CheckImageSide,
	getCheckDepositImage,
	getCheckDepositRequest,
} from "../../resources/checkDeposits"
import {useAccessToken, useIsUnitUser} from "../../services/auth"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {ErrorDocument, OkDocument, Resource} from "../../resources/common"
import moment from "moment"
import Icon from "../Icon/Icon"
import Skeleton from "react-loading-skeleton"
import {useModal} from "react-modal-hook"
import {RejectCheckModal} from "./RejectCheckModal"
import {ApproveCheckModal} from "./ApproveCheckModal"
import {ClearCheckModal} from "./ClearCheckModal"
import {ReturnCheckModal} from "./ReturnCheckModal"
import {kebabCase, startCase} from "lodash"
import classNames from "classnames"
import {DrawerHeader} from "../Drawer/Drawer"
import {KeyValueCard, KeyValueCardContainer, KeyValueCardKey, KeyValueCardValue} from "../KeyValueCard/KeyValueCard"
import {AsyncResultRequestState} from "../../types/asyncResult"
import {mapUsersIdToName} from "../../resources/user"
import {NamesAtUnitCard} from "../NamesAtUnit/NamesAtUnit"
import {isDepositOrFBOAccount} from "../../resources/account"
import {AccountCard} from "../Accounts/AccountCard"
import {CustomerCard} from "../Customers/CustomerCard"
import {Transactions, TransactionsColumns} from "../Transactions/Transactions"
import {CheckDepositColumns, CheckDeposits} from "./CheckDeposits"
import {UnitUserOnly} from "../../containers/PermissionedUser/PermissionedUser"
import {CheckImageCard} from "./CheckImageCard"
import {CheckDepositDetails} from "./CheckDepositDetails"
import {hasPermission} from "../../services/permission"

interface SingleCheckDepositProps {
	id: string
	token: string
	refresh: () => void
	useDrawer: boolean
	checkDepositAsyncResult?: AsyncResultRequestState<OkDocument<CheckDeposit>, ErrorDocument>
}

interface DecisionDetailsProps {
	decision: "Approved" | "Rejected"
	reason?: string
	reasonText?: string
	time?: Date
	user?: string
}

const internalDecisionReadyStatuses: Array<keyof typeof CheckDepositStatus> = [
	CheckDepositStatus.Pending,
	CheckDepositStatus.Rejected,
	CheckDepositStatus.Clearing,
	CheckDepositStatus.Sent,
	CheckDepositStatus.Canceled,
	CheckDepositStatus.Returned,
]

type ActionButtons = Array<"Reject" | "Approve" | "Return" | "Clear"> | null

const DecisionDetails: FC<DecisionDetailsProps> = ({decision, reason, time, user, reasonText}) => {
	return (
		<KeyValueCard title={"Decision Details"}>
			<KeyValueCardContainer>
				{decision && (
					<>
						<KeyValueCardKey> Decision</KeyValueCardKey>
						<KeyValueCardValue> {decision} </KeyValueCardValue>
					</>
				)}

				{reason && (
					<>
						<KeyValueCardKey> Reason</KeyValueCardKey>
						<KeyValueCardValue> {reasonText ? `${reason} (${reasonText})` : reason} </KeyValueCardValue>
					</>
				)}

				{time && (
					<>
						<KeyValueCardKey> Decision Date</KeyValueCardKey>
						<KeyValueCardValue> {moment(time).format("MM/DD/YYYY h:mm A")} </KeyValueCardValue>
					</>
				)}

				{user && (
					<>
						<KeyValueCardKey> Decisioned By</KeyValueCardKey>
						<KeyValueCardValue> {user} </KeyValueCardValue>
					</>
				)}
			</KeyValueCardContainer>
		</KeyValueCard>
	)
}

export function CheckActionButtons({
	id,
	refresh,
	buttons,
	disabled,
	title,
	vendor,
}: {
	id: string
	refresh: () => void
	buttons: ActionButtons
	disabled: boolean
	title: string
	vendor?: CheckDepositVendors
}) {
	if (!buttons) return null

	const [showRejectCheck, hideRejectCheck] = useModal(() => (
		<RejectCheckModal
			closeModal={hideRejectCheck}
			id={id}
			onSuccess={refresh}
			isVeryfi={vendor == CheckDepositVendors.Veryfi}
		/>
	))

	const [showApproveCheck, hideApproveCheck] = useModal(() => (
		<ApproveCheckModal closeModal={hideApproveCheck} id={id} onSuccess={refresh} />
	))

	const [showReturnCheck, hideReturnCheck] = useModal(() => (
		<ReturnCheckModal closeModal={hideReturnCheck} id={id} onSuccess={refresh} />
	))

	const [showClearCheck, hideClearCheck] = useModal(() => (
		<ClearCheckModal closeModal={hideClearCheck} id={id} onSuccess={refresh} />
	))

	return (
		<>
			{buttons.includes("Reject") && (
				<button className="button is-danger" onClick={() => showRejectCheck()} disabled={disabled} title={title}>
					<span className="icon">
						<Icon icon="interface-delete-interface-essential" size={12} />
					</span>
					<span> Reject </span>
				</button>
			)}

			{buttons.includes("Approve") && (
				<button className="button is-approved" onClick={() => showApproveCheck()} disabled={disabled} title={title}>
					<span className="icon">
						<Icon icon="interface-validation-check--interface-essential" size={12} />
					</span>
					<span> Approve </span>
				</button>
			)}

			{buttons.includes("Return") && (
				<button className="button is-danger" onClick={() => showReturnCheck()} disabled={disabled} title={title}>
					<span className="icon">
						<Icon icon="interface-validation-check--interface-essential" size={12} />
					</span>
					<span> Mark as returned </span>
				</button>
			)}

			{buttons.includes("Clear") && (
				<button className="button is-success" onClick={() => showClearCheck()} disabled={disabled} title={title}>
					<span className="icon">
						<Icon icon="interface-validation-check--interface-essential" size={12} />
					</span>
					<span> Clear Check </span>
				</button>
			)}
		</>
	)
}

function CheckDepositInfoPending() {
	return (
		<div className="check-content">
			<div className="check-image-card">
				<a className="single-check-deposit-link-with-icon">
					<Skeleton />
				</a>

				<div className="check-image-card-container">
					<div className={"skeleton-placeholder"}>
						<Skeleton />
					</div>
				</div>

				<div className="check-image-card-preview-container">
					<Skeleton />
					<div className="check-image-card-preview-container-wrapper">
						<Skeleton />
					</div>
				</div>
			</div>

			<KeyValueCard>
				<KeyValueCardContainer>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
				</KeyValueCardContainer>
			</KeyValueCard>

			<KeyValueCard>
				<KeyValueCardContainer>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
				</KeyValueCardContainer>
			</KeyValueCard>
		</div>
	)
}

export const getButtonsToShow = (
	status: keyof typeof CheckDepositStatus,
	vendor: CheckDepositVendors | undefined
): ActionButtons => {
	switch (status) {
		case CheckDepositStatus.PendingReview:
			return ["Approve", "Reject"]
		case CheckDepositStatus.Pending:
			return vendor == CheckDepositVendors.Veryfi ? ["Reject"] : null
		case CheckDepositStatus.Clearing:
			return ["Return", "Clear"]
		case CheckDepositStatus.Sent:
			return ["Return"]
		default:
			return null
	}
}

export const disabledActionsEnsentaTooltipValue = "Cannot perform actions for this vendor"

export function CheckDepositInfo({id, token, refresh, useDrawer, checkDepositAsyncResult}: SingleCheckDepositProps) {
	const accessToken = token ?? useAccessToken()
	const getCheckDeposit = checkDepositAsyncResult || useAsyncResult(() => getCheckDepositRequest({accessToken, id}))
	const frontImageState = useAsyncResult(() => getCheckDepositImage({accessToken, id, side: CheckImageSide.Front}))
	const backImageState = useAsyncResult(() => getCheckDepositImage({accessToken, id, side: CheckImageSide.Back}))
	const frontImage = frontImageState.match(
		() => undefined,
		(image) => image,
		() => null
	)
	const backImage = backImageState.match(
		() => undefined,
		(image) => image,
		() => null
	)

	return (
		<AsyncResultComponent asyncResult={getCheckDeposit} pendingComponent={<CheckDepositInfoPending />}>
			{({value}) => {
				const checkDeposit = value as {data: CheckDeposit; included: Resource[]}
				const checkId = checkDeposit.data.id
				const attributes = checkDeposit.data.attributes
				const relationships = checkDeposit.data.relationships
				const included = checkDeposit.included
				const vendor = attributes?.vendor
				const reason = attributes?.reason
				const reasonText = attributes?.reasonText
				const status = attributes?.status
				const updatedById = attributes?.statusSetBy
				const updatedAt = attributes?.statusCreatedAt
				const tags = attributes?.tags
				const userIdToName = mapUsersIdToName(included)
				const updateByName = updatedById ? userIdToName.get(updatedById) : undefined
				const depositAccount = included?.find((v) => v.type == "depositAccount")

				return (
					<>
						{useDrawer && (
							<DrawerHeader title={`Check #${id}`}>
								<div className={classNames("check-deposit-status", kebabCase(status))}>{startCase(status)}</div>
							</DrawerHeader>
						)}
						<div className="check-content">
							{useDrawer && (
								<CheckImageCard frontImage={frontImage} backImage={backImage} id={checkId} status={attributes.status} />
							)}
							<CheckDepositDetails
								amount={attributes.amount}
								number={attributes.checkNumber}
								customerId={relationships.customer?.data.id}
								accountId={relationships.account?.data.id}
								depositDate={attributes.createdAt}
								description={attributes.description}
								include={included}
								settlementDate={attributes.settlementDate}
								tags={tags}
								orgId={useIsUnitUser() ? relationships.org?.data.id : undefined}
							/>
							{!useDrawer && (
								<>
									{relationships.customer?.data.id && (
										<UnitUserOnly>
											<NamesAtUnitCard customerId={`${relationships.customer?.data.id || ""}`} />
										</UnitUserOnly>
									)}
									{depositAccount && isDepositOrFBOAccount(depositAccount) && (
										<AccountCard account={depositAccount} accessToken={accessToken} />
									)}
									{relationships.customer?.data.id && (
										<CustomerCard customerId={relationships.customer.data.id.toString()} />
									)}
									{attributes.status === "PendingReview" && (
										<>
											<CheckDeposits
												title={"Recent Check Deposits"}
												fullHeight={false}
												stickyAction={false}
												enablePaging={false}
												enableStatusFilter={false}
												enableDateFilter={false}
												enableSearch={false}
												enableExport={false}
												enableRowClick={false}
												enableTitle
												disableDrawer
												limit={10}
												includedColumns={[
													CheckDepositColumns.id,
													CheckDepositColumns.status,
													CheckDepositColumns.reason,
													CheckDepositColumns.depositDate,
													CheckDepositColumns.amount,
												]}
												customerId={relationships.customer?.data.id.toString()}
											/>
											<Transactions
												enableAmountFilter={false}
												enableDateFilter={false}
												enableTypeFilter={false}
												enableOrgFilter={false}
												enableDirectionFilter={false}
												isUsingPaging={false}
												fullHeight={false}
												accountId={`${relationships.account?.data.id}`}
												limit={5}
												enableSearch={false}
												enableExport={false}
												title={"Recent Transactions"}
												token={accessToken}
												includedColumns={[
													TransactionsColumns.id,
													TransactionsColumns.type,
													TransactionsColumns.summary,
													TransactionsColumns.createdAt,
													TransactionsColumns.balance,
													TransactionsColumns.amount,
												]}
											/>
										</>
									)}
								</>
							)}
							{internalDecisionReadyStatuses.includes(status) && (
								<DecisionDetails
									time={updatedAt}
									user={updateByName}
									decision={status == CheckDepositStatus.Rejected ? CheckDepositStatus.Rejected : "Approved"}
									reason={reason}
									reasonText={reasonText}
								/>
							)}
						</div>
						{useDrawer && hasPermission("checkDeposit.approve", "update") && (
							<div className="drawer-action-buttons buttons is-right">
								<CheckActionButtons
									id={checkId}
									refresh={refresh}
									buttons={getButtonsToShow(status, vendor)}
									title={vendor === CheckDepositVendors.Ensenta ? disabledActionsEnsentaTooltipValue : ""}
									disabled={vendor === CheckDepositVendors.Ensenta}
									vendor={vendor}
								/>
							</div>
						)}
					</>
				)
			}}
		</AsyncResultComponent>
	)
}
